import '../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import logoSmall from '../../assets/lucky_icon_title_small.svg';

const LAUNCH_DATE_ISO = '2026-08-24T07:00:00Z';
const TINYLAUNCH_URL = 'https://www.tinylaunch.com/launch/18402-luckynote';
const CHANGELOG_URL = 'https://luckynote.io/changelog';
const APP_URL = 'https://luckynote.io';

const getTimeLeft = () => {
    const diff = new Date(LAUNCH_DATE_ISO).getTime() - Date.now();

    if (diff <= 0) {
        return {
            launched: true,
            days: '00',
            hours: '00',
            minutes: '00',
            seconds: '00'
        };
    }

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
        launched: false,
        days: String(days).padStart(2, '0'),
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0')
    };
};

const buildCalendarUrl = () => {
    const title = encodeURIComponent('Support Luckynote on TinyLaunch');
    const details = encodeURIComponent('Luckynote is launching on TinyLaunch. Visit https://www.tinylaunch.com/launch/18402-luckynote to support the launch.');
    const location = encodeURIComponent(TINYLAUNCH_URL);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=20260824T070000Z/20260824T080000Z&details=${details}&location=${location}`;
};

const LaunchPage = () => {
    const {t: lnT} = useTranslation();

    const [timeLeft, setTimeLeft] = useState(getTimeLeft);
    const calendarUrl = useMemo(buildCalendarUrl, [lnT]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setTimeLeft(getTimeLeft());
        }, 1000);

        return () => clearInterval(intervalId);
    }, []);

    return (
        <Page>
            <Main>
                <LogoLink href={APP_URL} aria-label={lnT('common:luckynoteHome')}>
                    <img src={logoSmall} alt={lnT('common:luckynote')} />
                </LogoLink>

                <Badge>{lnT('common:launchingOnTinylaunch')}</Badge>

                <h1>{lnT('common:luckynote2026')}</h1>
                <Intro>
                    <span>{lnT('common:theLaunchIsAlmostHere')}</span>
                    <span>{lnT('common:ifLuckynoteHasHelpedYouSaveSomething')}</span>
                </Intro>

                <Countdown aria-label={lnT('common:launchCountdown')}>
                    <TimeBlock>
                        <strong>{timeLeft.days}</strong>
                        <span>{lnT('common:days')}</span>
                    </TimeBlock>
                    <Separator>:</Separator>
                    <TimeBlock>
                        <strong>{timeLeft.hours}</strong>
                        <span>{lnT('common:hours')}</span>
                    </TimeBlock>
                    <Separator>:</Separator>
                    <TimeBlock>
                        <strong>{timeLeft.minutes}</strong>
                        <span>{lnT('common:mins')}</span>
                    </TimeBlock>
                    <Separator>:</Separator>
                    <TimeBlock>
                        <strong>{timeLeft.seconds}</strong>
                        <span>{lnT('common:secs')}</span>
                    </TimeBlock>
                </Countdown>

                <LaunchTime>{lnT('common:august2420260900MadridTime')}</LaunchTime>

                <CtaPanel>
                    <PrimaryLink
                        href={timeLeft.launched ? TINYLAUNCH_URL : calendarUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {timeLeft.launched ? lnT('common:supportUsOnTinylaunch') : lnT('common:addToCalendar')}
                    </PrimaryLink>
                </CtaPanel>

                <SecondaryActions>
                    {!timeLeft.launched ? (
                        <SecondaryLink href={TINYLAUNCH_URL} target="_blank" rel="noopener noreferrer">{lnT('common:viewOnTinylaunch')}</SecondaryLink>
                    ) : null}
                    <SecondaryLink href={CHANGELOG_URL}>{lnT('common:seeWhatChanged')}</SecondaryLink>
                </SecondaryActions>
            </Main>
        </Page>
    );
};

const Page = styled.div`
    min-height: 100vh;
    overflow: hidden;
    background:
        radial-gradient(circle at 50% -10%, rgba(250, 230, 164, 0.5), transparent 35%),
        linear-gradient(180deg, #fffaf0 0%, #f6f7fb 100%);
    color: #101114;
    font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Main = styled.main`
    width: min(760px, calc(100% - 40px));
    min-height: 100vh;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 42px 0;
    text-align: center;

    h1 {
        max-width: 720px;
        margin: 18px 0 0;
        font-size: clamp(44px, 8vw, 74px);
        line-height: 1.02;
        font-weight: 800;
        letter-spacing: 0;
    }
`;

const LogoLink = styled.a`
    display: inline-flex;
    align-items: center;
    color: inherit;
    text-decoration: none;
    
    img {
        display: block;
        width: 180px;
        height: auto;
    }
`;

const Badge = styled.div`
    margin-top: 26px;
    border-radius: 999px;
    background: #ff6154;
    color: #ffffff;
    padding: 8px 16px;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
`;

const Intro = styled.p`
    max-width: 650px;
    margin: 18px 0 0;
    color: #4d515a;
    font-size: 18px;
    line-height: 1.6;

    span {
        display: block;
    }

    span + span {
        margin-top: 10px;
    }
`;

const Countdown = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 12px;
    margin-top: 34px;

    @media (max-width: 560px) {
        gap: 6px;
    }
`;

const TimeBlock = styled.div`
    width: clamp(64px, 16vw, 116px);
    aspect-ratio: 1 / 1;
    border: 1px solid rgba(16, 17, 20, 0.08);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.78);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 30px rgba(16, 17, 20, 0.08);

    strong {
        font-size: clamp(30px, 7vw, 54px);
        line-height: 1;
        font-weight: 800;
    }

    span {
        margin-top: 8px;
        color: #777b84;
        font-size: 11px;
        font-weight: 800;
        text-transform: uppercase;
    }
`;

const Separator = styled.span`
    padding-top: clamp(18px, 5vw, 38px);
    font-size: clamp(24px, 5vw, 42px);
    font-weight: 700;
    color: #777b84;
`;

const LaunchTime = styled.div`
    margin-top: 18px;
    color: #666a73;
    font-size: 15px;
`;

const CtaPanel = styled.div`
    margin-top: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const PrimaryLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 52px;
    border-radius: 8px;
    background: #ff6154;
    color: #ffffff;
    padding: 0 24px;
    font-size: 15px;
    font-weight: 800;
    text-decoration: none;
`;

const SecondaryActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 22px;
`;

const SecondaryLink = styled.a`
    color: #101114 !important;
    font-size: 14px;
    font-weight: 700;
    text-decoration: underline;
`;

export default LaunchPage;
